import { FilaDeGrafiasPage } from '../catalogo-veiculos/FilaDeGrafiasPage';
import { SincronizacaoPage } from '../catalogo-veiculos/SincronizacaoPage';
import { CicloInscricaoPage } from '../CicloInscricaoPage';
import { CentroDeParametrizacao } from './CentroDeParametrizacao';
import { ParametrosAprovacaoDtegPage } from './ParametrosAprovacaoDtegPage';
import { PrazosCalendarioHub } from './PrazosCalendarioHub';
import { WorkflowsAprovacoesHub } from './WorkflowsAprovacoesHub';
import { DEFINICOES_DE_CADASTRO, definicaoDoCadastro } from './cadastros.definicoes';
import type { ChaveDeDominio } from './dados';
import { EscolaSomenteConsulta } from './EscolaSomenteConsulta';
import { FormularioDeCadastro } from './FormularioDeCadastro';
import type { ChaveDeContagem } from './hubs';
import { ListaDeCadastro } from './ListaDeCadastro';
import { PaginaDeHub } from './PaginaDeHub';
import { TelaDeCadastro } from './TelaDeCadastro';
import { ValidacaoDeDocumentos } from './ValidacaoDeDocumentos';
import { TelaDeDominio, type ColunaDeDominio } from './TelaDeDominio';

/**
 * Todas as telas de `/admin/parametros`, por caminho.
 *
 * Fica separado do `App.tsx` porque são mais de vinte entradas e o roteador não
 * deve virar um catálogo. O `App.tsx` só espalha este objeto dentro de
 * `TELAS_PRONTAS` — a rota continua nascendo do mapa de navegação, como manda
 * `nucleo/navegacao/mapa.ts`.
 */

const CADASTROS = '/admin/parametros/cadastros';
const DOCUMENTOS = '/admin/parametros/documentos';
const FINANCEIRO = '/admin/parametros/financeiro';
const TABELA_DE_PRECOS = '/operacao/financeiro/tabela-precos';

/**
 * Frase de apoio dos três cartões que levam à Tabela de Preços Contratuais.
 *
 * O cartão leva o nome da ABA de destino, para origem e destino se chamarem do
 * mesmo jeito; a frase é onde o nome canônico da tela continua visível, para
 * quem chega pelo hub saber que os três levam à mesma tela.
 */
const ABAS_DA_TABELA_DE_PRECOS: Readonly<Record<string, string>> = {
  [TABELA_DE_PRECOS]:
    'Aba da Tabela de Preços Contratuais: valores por tipo de bancada, versionados por vigência.',
  [`${TABELA_DE_PRECOS}?aba=retencoes`]:
    'Aba da Tabela de Preços Contratuais: retenções de INSS e ISS aplicadas à remuneração.',
  [`${TABELA_DE_PRECOS}?aba=ir`]:
    'Aba da Tabela de Preços Contratuais: faixas progressivas do imposto de renda retido.',
};

/** Dinheiro em real, como manda o design system: sempre com duas casas. */
function emReais(valor: unknown): string {
  const numero = Number(valor);
  return Number.isFinite(numero)
    ? numero.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—';
}

interface Dominio {
  caminho: string;
  titulo: string;
  descricao: string;
  chave: ChaveDeDominio;
  colunas: readonly ColunaDeDominio[];
  hub: string;
  rotuloDoHub: string;
  /** Aviso informativo do protótipo, quando existe. */
  aviso?: string;
}

/**
 * Título, colunas e hub dos domínios que ainda leem o dado temporário
 * (documentos e financeiro). Os cadastros de referência, que leem a API, estão em
 * `cadastros.definicoes.tsx`.
 *
 * Exportado por causa de `paridade.spec.ts`: o teste confere estes rótulos
 * contra o que o protótipo escreve, e para isso precisa lê-los da fonte, não
 * de uma cópia. Cópia foi exatamente o que deixou vinte cabeçalhos errados em
 * Análises passarem pela revisão.
 */
export const DOMINIOS: readonly Dominio[] = [
  // ---- Hub 3 — Documentos e compliance ----
  {
    caminho: `${DOCUMENTOS}/requisitos`,
    titulo: 'Requisitos documentais',
    descricao:
      'Documentos exigidos por entidade, com validade padrão, antecedência de alerta e tamanho máximo de arquivo.',
    chave: 'requisitosDocumentais',
    colunas: [
      { campo: 'nome', titulo: 'Nome' },
      { campo: 'categoria', titulo: 'Categoria' },
      { campo: 'validade', titulo: 'Validade' },
    ],
    hub: DOCUMENTOS,
    rotuloDoHub: 'Documentos e compliance',
  },
  // `${DOCUMENTOS}/cursos` foi removido daqui: era a MESMA tela de
  // `${CADASTROS}/cursos-obrigatorios` — mesma chave `cursosObrigatorios`,
  // mesmas colunas — só com outro endereço e outro título. O sub-hub de
  // documentos agora aponta para o cadastro, como faz o protótipo, em vez de
  // manter duas cópias da mesma listagem (decisão do dono, 2026-09-04).
  {
    caminho: `${DOCUMENTOS}/apolices`,
    titulo: 'Apólices por tipo de veículo',
    descricao: 'Cobertura mínima de seguro exigida de cada tipo de veículo.',
    chave: 'apolices',
    colunas: [
      { campo: 'nome', titulo: 'Nome' },
      {
        campo: (linha) => emReais(linha.coberturaMinima),
        titulo: 'Cobertura mínima',
        numerica: true,
        ordenarPor: 'coberturaMinima',
      },
    ],
    hub: DOCUMENTOS,
    rotuloDoHub: 'Documentos e compliance',
  },

  // ---- Hub 4 — Financeiro e tributário ----
  {
    caminho: `${FINANCEIRO}/bases-calculo-multa`,
    titulo: 'Bases de cálculo de multa',
    descricao: 'Como o valor de uma multa é apurado: percentual, valor fixo ou múltiplo.',
    chave: 'basesCalculoMulta',
    colunas: [
      { campo: 'nome', titulo: 'Nome' },
      { campo: 'codigo', titulo: 'Código' },
      { campo: 'formula', titulo: 'Fórmula' },
    ],
    hub: FINANCEIRO,
    rotuloDoHub: 'Financeiro e tributário',
  },
  {
    caminho: `${FINANCEIRO}/tipos-glosa`,
    titulo: 'Tipos de glosa',
    descricao: 'Catálogo padronizado de glosas aplicáveis em medição.',
    chave: 'tiposGlosa',
    colunas: [
      { campo: 'nome', titulo: 'Nome' },
      { campo: 'percentual', titulo: '%', numerica: true },
      { campo: 'base', titulo: 'Base' },
    ],
    hub: FINANCEIRO,
    rotuloDoHub: 'Financeiro e tributário',
  },
  {
    caminho: `${FINANCEIRO}/capacidade-veiculo`,
    titulo: 'Capacidade por veículo',
    descricao:
      'Capacidade máxima padrão de crianças por tipo de veículo. Quando o apontamento ultrapassar essa capacidade, o sistema exige confirmação do operador e registra log. Valor 0 desativa a validação para o tipo.',
    chave: 'capacidadeVeiculo',
    colunas: [
      { campo: 'nome', titulo: 'Nome' },
      {
        // Rótulo e unidade do protótipo (`CapacidadeVeiculoPage.tsx`): coluna
        // "Capacidade", valor "{capacidade_padrao} crianças" — não "Capacidade
        // máxima" nem só o número.
        campo: (linha) => `${String(linha.capacidadeMaxima ?? 0)} crianças`,
        titulo: 'Capacidade',
        numerica: true,
        ordenarPor: 'capacidadeMaxima',
      },
    ],
    hub: FINANCEIRO,
    rotuloDoHub: 'Financeiro e tributário',
  },
  {
    caminho: `${FINANCEIRO}/documentos-pagamento`,
    titulo: 'Documentos de pagamento',
    descricao: 'O que o credenciado precisa enviar para receber, e em quantos dias.',
    chave: 'documentosPagamento',
    colunas: [
      { campo: 'nome', titulo: 'Nome' },
      { campo: 'categoria', titulo: 'Categoria' },
      { campo: 'validade', titulo: 'Validade' },
    ],
    hub: FINANCEIRO,
    rotuloDoHub: 'Financeiro e tributário',
  },
];

/** Qual domínio alimenta o contador de cada tela, no cartão do hub. */
const DOMINIO_POR_TELA: Readonly<Record<string, ChaveDeContagem>> = {
  ...Object.fromEntries(DOMINIOS.map((dominio) => [dominio.caminho, dominio.chave])),
  ...Object.fromEntries(DEFINICOES_DE_CADASTRO.map((d) => [d.caminho, d.chave])),
};

// ---- Hub 2 — Cadastros de referência ----
const REFERENCIA = '/admin/parametros/referencia';
const ESCOLAS = `${REFERENCIA}/escolas`;
const SEGURADORAS = `${REFERENCIA}/seguradoras`;

const ESCOLA = definicaoDoCadastro('escolas');
const SEGURADORA = definicaoDoCadastro('seguradoras');

export const TELAS_DE_PARAMETRIZACAO: Readonly<Record<string, React.ReactNode>> = {
  '/admin/parametros': <CentroDeParametrizacao />,
  // Os dois sub-hubs escritos à mão. Os outros quatro nascem do
  // espalhamento de HUBS, logo abaixo; estes têm cartões próprios porque
  // reúnem telas que não são de domínio.
  '/admin/parametros/prazos': <PrazosCalendarioHub />,
  '/admin/parametros/workflows': <WorkflowsAprovacoesHub />,
  // Telas filhas destes dois sub-hubs que não são de domínio, e por isso
  // não nascem do espalhamento abaixo.
  '/admin/parametros/ciclos-inscricao': <CicloInscricaoPage />,
  '/admin/parametros/aprovacoes': <ParametrosAprovacaoDtegPage />,
  [CADASTROS]: (
    <PaginaDeHub
      caminho={CADASTROS}
      dominioPorTela={DOMINIO_POR_TELA}
      formatoContador="apenas-ativos"
    />
  ),
  [DOCUMENTOS]: <PaginaDeHub caminho={DOCUMENTOS} dominioPorTela={DOMINIO_POR_TELA} />,
  [FINANCEIRO]: (
    <PaginaDeHub
      caminho={FINANCEIRO}
      dominioPorTela={DOMINIO_POR_TELA}
      descricaoPorTela={ABAS_DA_TABELA_DE_PRECOS}
    />
  ),
  [`${FINANCEIRO}/validacao-documentos`]: <ValidacaoDeDocumentos />,
  // Marcas, modelos e versões de veículo: fila de grafias e sincronização com a
  // fonte oficial (módulo catalogo-veiculos).
  [`${CADASTROS}/grafias-veiculo`]: <FilaDeGrafiasPage />,
  [`${CADASTROS}/sincronizacao-veiculos`]: <SincronizacaoPage />,
  [REFERENCIA]: (
    <PaginaDeHub
      caminho={REFERENCIA}
      dominioPorTela={DOMINIO_POR_TELA}
      descricaoPorTela={{
        [ESCOLAS]:
          'Unidades escolares (com código EOL) referenciadas por Demandas e Ordens de Serviço.',
        [SEGURADORAS]: 'Empresas seguradoras referenciadas pelas apólices de veículos.',
      }}
      formatoContador="ativos-e-total"
    />
  ),

  [ESCOLAS]: <ListaDeCadastro definicao={ESCOLA} />,
  // Escola só se consulta: o cadastro vem do EOL. As duas rotas abaixo seguem no
  // mapa de navegação e dizem isso, em vez de abrir um formulário que não grava.
  [`${ESCOLAS}/nova`]: <EscolaSomenteConsulta definicao={ESCOLA} />,
  [`${ESCOLAS}/:slug`]: <TelaDeCadastro definicao={ESCOLA} />,
  [`${ESCOLAS}/:slug/editar`]: <EscolaSomenteConsulta definicao={ESCOLA} />,

  [SEGURADORAS]: <ListaDeCadastro definicao={SEGURADORA} />,
  [`${SEGURADORAS}/nova`]: <FormularioDeCadastro modo="criar" definicao={SEGURADORA} />,
  [`${SEGURADORAS}/:slug`]: <TelaDeCadastro definicao={SEGURADORA} />,
  [`${SEGURADORAS}/:slug/editar`]: <FormularioDeCadastro modo="editar" definicao={SEGURADORA} />,

  // Os treze cadastros de domínio, lidos e gravados pela API.
  ...Object.fromEntries(
    DEFINICOES_DE_CADASTRO.filter((d) => d.grupo === 'cadastros').map((d) => [
      d.caminho,
      <ListaDeCadastro key={d.caminho} definicao={d} />,
    ]),
  ),
  ...Object.fromEntries(
    DOMINIOS.map((dominio) => [
      dominio.caminho,
      <TelaDeDominio
        key={dominio.caminho}
        titulo={dominio.titulo}
        descricao={dominio.descricao}
        chave={dominio.chave}
        colunas={dominio.colunas}
        voltarPara={dominio.hub}
        rotuloDoVoltar={dominio.rotuloDoHub}
        aviso={dominio.aviso}
      />,
    ]),
  ),
};
